import { atom } from 'jotai';
import { atomWithStorage, loadable } from 'jotai/utils';
import { isEqual } from 'lodash';
import { savedViewsAtom as apiSavedViewsAtom } from '~/atoms/savedViews';
import { linePlanEntityAtom } from '~/atoms/linePlanEntity';
/**
 * Atom to store the grid API reference for global access
 */
export const gridApiAtom = atom(null);
/**
 * Current active column state for the grid
 * This tracks the live state as users interact with the grid
 */
export const gridColumnStateAtom = atom(null);
/**
 * Currently selected/active saved view IDs per line plan entity type
 * Stores separate view selections for project, model, and style tabs
 */
export const activeViewIdsByTypeAtom = atomWithStorage('activeViewIdsByType', {
    project: null,
    model: null,
    style: null,
    expression: null,
    product: null
});
/**
 * Derived atom to get the active view ID for the current line plan entity type
 */
export const activeViewIdAtom = atom((get) => {
    const linePlanEntity = get(linePlanEntityAtom);
    const viewIdsByType = get(activeViewIdsByTypeAtom);
    return viewIdsByType[linePlanEntity];
}, (get, set, newValue) => {
    const linePlanEntity = get(linePlanEntityAtom);
    const currentViewIds = get(activeViewIdsByTypeAtom);
    set(activeViewIdsByTypeAtom, {
        ...currentViewIds,
        [linePlanEntity]: newValue
    });
});
const loadableApiSavedViews = loadable(apiSavedViewsAtom);
/**
 * Derived atom to get the currently active saved view
 * Uses API-based saved views for consistency with useSavedViews hook
 */
export const activeViewAtom = atom((get) => {
    const savedViewsResult = get(loadableApiSavedViews);
    const activeViewId = get(activeViewIdAtom);
    if (!activeViewId || savedViewsResult.state !== 'hasData')
        return null;
    return (savedViewsResult.data.find((view) => view.projectId === activeViewId) ||
        null);
});
/**
 * Atom to track if grid state has unsaved changes
 */
export const hasUnsavedChangesAtom = atom((get) => {
    const currentState = get(gridColumnStateAtom);
    const activeView = get(activeViewAtom);
    if (!currentState || !activeView)
        return false;
    return !isEqual(currentState, activeView.metadata.gridState);
});
